import { RouterButton } from 'components/Button/RouterButton';
import { Icon } from 'components/Icon';
import { SimpleTooltip } from 'components/SimpleTooltip';
import { BodySmall } from 'components/Text/BodySmall';
import { H5 } from 'components/Text/H5';
import { showModal } from 'modules/main/store/ducks/modal';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { listingSearchHomePath } from 'utils/searchHomePages';

import {
  CmsSocialSidebarContainer,
  CmsSocialSidebarRecentJobsContainer,
  CmsSocialSidebarTooltipContainer,
} from './CmsSocialSidebar.styles';

type Props = {
  headline?: string;
  description?: string;
  hasRecentJobs: boolean;
  hasSocialShare: boolean;
  subscribe: () => Promise<void>;
  canSubscribe: boolean;
  isSubscribed: boolean;
  shareUrl: Record<UserLocale, string>;
  subsiteSubscribeText: string;
};

export function CmsSocialSidebar({
  headline = getText('Land Your Dream Job'),
  canSubscribe,
  isSubscribed,
  subscribe,
  description = getText('Careers that help you move from intention to action'),
  hasRecentJobs,
  hasSocialShare,
  shareUrl,
  subsiteSubscribeText,
}: Props) {
  const dispatch = useMainDispatch();

  function handleShare() {
    dispatch(
      showModal('SHARE_LISTING', {
        listing: { type: 'URL' },
        listingType: 'URL',
        messageOverride: getText('Check out this article'),
        shareUrl,
      }),
    );
  }

  return (
    <CmsSocialSidebarContainer $hasRecentJobs={hasRecentJobs}>
      {hasRecentJobs ? (
        <CmsSocialSidebarRecentJobsContainer>
          <H5 mb={0}>{headline}</H5>
          <BodySmall lineHeight={1.2} mb="10px">
            {description}
          </BodySmall>
          <RouterButton
            data-qa-id="sidebar-recent-jobs-button"
            onClick={() => trackClicked('Search Jobs', { context: 'CMS Page' })}
            to={listingSearchHomePath('JOB')}
            variant="primary"
            size="small"
          >
            {getText('Search Jobs')}
          </RouterButton>
        </CmsSocialSidebarRecentJobsContainer>
      ) : null}
      <CmsSocialSidebarTooltipContainer>
        {canSubscribe ? (
          <SimpleTooltip
            text={
              isSubscribed
                ? getText('You are now subscribed!')
                : subsiteSubscribeText
            }
          >
            <button
              type="button"
              onClick={subscribe}
              data-qa-id="sidebar-subscribe-to-subsite"
              disabled={isSubscribed}
            >
              <Icon
                initialColorName="lightContentGrey"
                name="bell"
                size={24}
                keepColorOnLoad
              />
            </button>
          </SimpleTooltip>
        ) : null}
        {hasSocialShare ? (
          <SimpleTooltip text={getText('Share')} showArrow>
            <button
              type="button"
              onClick={handleShare}
              data-qa-id="sidebar-social-share-button"
            >
              <Icon
                initialColorName="lightContentGrey"
                name="share"
                size={24}
                keepColorOnLoad
              />
            </button>
          </SimpleTooltip>
        ) : null}
      </CmsSocialSidebarTooltipContainer>
    </CmsSocialSidebarContainer>
  );
}
